// src/components/authentication/ForgotPasswordRequest.js
/* ------------------------------------------------------------------ */
/* ForgotPasswordRequest – send reset-link e-mail                      */
/* ------------------------------------------------------------------ */
/**
 * • Collects user’s e-mail → `sendPasswordResetEmail()`.
 * • Shows confirmation that a link was sent (even if the e-mail is unknown,
 *   to avoid account enumeration).
 */

import React, { useState } from "react";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { Link } from "react-router-dom";
import { firebaseApp } from "../../firebase/firebaseApp";
import { EMAIL_REGEX } from "../../utils/constants";
import usePopup from "../../hooks/usePopup";
import ErrorPopup from "../ErrorPopup";

/* Firebase singleton */
const auth = getAuth(firebaseApp);

/* Component */
export default function ForgotPasswordRequest() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const { popup, open: openPopup, close: closePopup } = usePopup();

  const handleSubmit = async (e) => {
    e.preventDefault();
    closePopup();

    if (!EMAIL_REGEX.test(email)) {
      openPopup({ message: "Please enter a valid email address." });
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email, {
        // optional: customise Firebase-hosted page URL
        url: `${window.location.origin}/auth/reset-password`,
        handleCodeInApp: true,
      });
      setSent(true);
    } catch (err) {
      openPopup({ message: "Failed to send reset e-mail. Please try again." });
    }
  };

  return (
    <div className="w-full max-w-lg h-full p-4 flex flex-col justify-end md:justify-start items-center gap-4">
      {popup && <ErrorPopup popup={popup} onClose={closePopup} />}

      <img src="/pdx_logo.png" alt="pDx logo" className="h-24" />
      <h1 className="title-text">Reset password</h1>

      {sent ? (
        <>
          <p className="text-center">
            If an account exists for <strong>{email}</strong> you'll receive a
            message with a link to reset your password.
          </p>
          <Link to="/auth/login" className="link">
            Back to login
          </Link>
        </>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full">
          <input
            type="email"
            placeholder="Email"
            className="custom-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <button type="submit" className="primary-button">
            Send reset link
          </button>
        </form>
      )}
    </div>
  );
}
